import React, { useState } from 'react';

import { useQuery, useMutation } from 'react-query';

import Library from '../../components/Library';
import FolderService from '../../services/FolderService';
import MeetingService from '../../services/MeetingService';
import useFetchingState from '../../components/Library/useFetchingState';
import CreateMeetingMobileButton from '../../components/CreateMeetingMobileButton';
import { useUiContext } from '../../context/UiContext';

export default function LibraryPage() {
  const [isProcessing, setIsProcessing] = useState(false);
  const { notifyError } = useUiContext();

  const {
    page,
    setPage,
    searchInput,
    setSearchInput,
    sortField,
    setSortField,
    sortOrder,
    setSortOrder,
    debouncedSearchInput,
    navigate,
    meetingId,
    isShare,
    id,
    isMobile,
    itemsRange,
    meetingsPage,
    setMeetingsPage,
  } = useFetchingState();

  const {
    data: folders,
    refetch: refetchFolders,
    isFetching: areFoldersFetching,
  } = useQuery(
    ['folders', page, debouncedSearchInput, sortField, sortOrder],
    () =>
      FolderService.getFolders({
        search: debouncedSearchInput,
        limit: itemsRange,
        sortField,
        sortOrder,
        page,
      }),
    {
      keepPreviousData: true,
      enabled: !id,
    }
  );

  const {
    data: openedFolder,
    refetch: refetchFolder,
    isFetching: isFolderFetching,
  } = useQuery(
    [
      'folders',
      id,
      debouncedSearchInput,
      sortField,
      sortOrder,
      meetingsPage,
      itemsRange,
    ],
    () =>
      FolderService.getFolder({
        id,
        search: debouncedSearchInput,
        sortField: sortField === 'name' ? 'title' : sortField,
        sortOrder,
        page: meetingsPage,
        limit: itemsRange,
      }),
    {
      keepPreviousData: true,
      enabled: !!id,
      retry: false,
      onError: (error) => {
        if (error?.response?.status === 403) {
          navigate(
            `/request-folder-access/${id}${
              meetingId ? `?meetingId=${meetingId}` : ''
            }`,
            { replace: true }
          );
        }
      },
      onSuccess: (data) => {
        if (
          data?.meetings?.some((meeting) => meeting.status === 'in progress')
        ) {
          setIsProcessing(true);
        } else {
          setIsProcessing(false);
        }
      },
      refetchInterval: isProcessing ? 10000 : false,
    }
  );

  const { mutate: toggleFolderTrashStatus } = useMutation(
    FolderService.toggleFolderTrashStatus,
    {
      onSuccess: () => {
        if (openedFolder) {
          refetchFolder();
        } else {
          refetchFolders();
        }
      },
    }
  );

  const { mutate: toggleMeetingTrashStatusMutation } = useMutation(
    MeetingService.toggleMeetingTrashStatus,
    {
      onSuccess: () => {
        refetchFolder();
      },
      onError: (error) => {
        notifyError(
          error?.response?.data?.message || 'Failed to move video to trash'
        );
      },
    }
  );

  const { mutate: deleteMeetingMutation } = useMutation(
    MeetingService.deleteMeeting,
    {
      onSuccess: () => {
        refetchFolder();
      },
      onError: (error) => {
        notifyError(
          error?.response?.data?.message || 'Failed to delete video'
        );
      },
    }
  );

  const { mutate: updateFolderMutation } = useMutation(
    FolderService.updateFolder,
    {
      onSuccess: () => {
        if (openedFolder) {
          refetchFolder();
        } else {
          refetchFolders();
        }
      },
      onError: (error) => {
        notifyError(
          error?.response?.data?.message || 'Failed to update folder'
        );
      },
    }
  );

  const { mutateAsync: moveFolderMutation } = useMutation(
    ({ folderId, destinationFolderId }) =>
      FolderService.moveFolder(folderId, destinationFolderId),
    {
      onSuccess: () => {
        if (openedFolder) {
          refetchFolder();
        } else {
          refetchFolders();
        }
      },
      onError: (error) => {
        notifyError(
          error?.response?.data?.message || 'Failed to move folder'
        );
      },
    }
  );

  const { mutateAsync: moveMeetingMutation } = useMutation(
    ({ meetingId: moveMeetingId, destinationFolderId }) =>
      MeetingService.moveMeeting(moveMeetingId, destinationFolderId),
    {
      onSuccess: () => {
        refetchFolder();
      },
      onError: (error) => {
        notifyError(
          error?.response?.data?.message || 'Failed to move video'
        );
      },
    }
  );

  return (
    <>
      <Library
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        isMobile={isMobile}
        page={page}
        setPage={setPage}
        meetingsPage={meetingsPage}
        setMeetingsPage={setMeetingsPage}
        sortField={sortField}
        setSortField={setSortField}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        debouncedSearchInput={debouncedSearchInput}
        refetchFolders={refetchFolders}
        refetchFolder={refetchFolder}
        areFoldersFetching={areFoldersFetching}
        isFolderFetching={isFolderFetching}
        folders={folders}
        openedFolder={openedFolder}
        id={id}
        isShare={isShare}
        itemsRange={itemsRange}
        moveFolderToTrashMutation={toggleFolderTrashStatus}
        restoreFolderMutation={toggleFolderTrashStatus}
        moveToTrashMeetingMutation={toggleMeetingTrashStatusMutation}
        restoreMeetingMutation={toggleMeetingTrashStatusMutation}
        deleteMeetingMutation={deleteMeetingMutation}
        updateFolderMutation={updateFolderMutation}
        moveFolderMutation={moveFolderMutation}
        moveMeetingMutation={moveMeetingMutation}
        breadcrumbLabel="My Library"
        breadcrumbTo="/my-library"
      />
      <CreateMeetingMobileButton />
    </>
  );
}
